import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Box, Typography, Button, Container, Grid, Paper, Stack, Avatar } from '@mui/material';
import { ErrorContainer } from './ErrorContainer';
import { Link } from 'react-router-dom';

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    const handleReload = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        // Use window.location.href instead of useNavigate to avoid Router context issues
        // during static site generation (react-snap)
        window.location.href = '/';
    };

    return (
        <Box width="100%" minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
            <ErrorContainer maxWidth="md" component="section">
                <Container>
                    <Paper sx={{ p: 4 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} textAlign="center">
                                <Link to="/">
                                    <Avatar
                                        variant="square"
                                        src="/images/logo.svg"
                                        sx={{ height: 60, width: 60, mx: 'auto', mb: 2, cursor: 'pointer' }}
                                        alt="Logo"
                                    />
                                </Link>
                                <Typography variant="h3" gutterBottom>
                                    Oops! Something went wrong
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    We're sorry, but something unexpected happened. Please try refreshing the page or go back to the homepage.
                                </Typography>
                                <Stack spacing={2} direction="row" justifyContent="center" mt={4}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={resetErrorBoundary}
                                    >
                                        Try Again
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleReload}
                                    >
                                        Reload Page
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleGoHome}
                                    >
                                        Go to Home
                                    </Button>
                                </Stack>

                                {process.env.NODE_ENV === 'development' && error && (
                                    <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, width: '100%', textAlign: 'left' }}>
                                        <Typography variant="h6" color="error" gutterBottom>
                                            Development Error Details:
                                        </Typography>
                                        <Typography variant="body2" component="pre" sx={{
                                            overflow: 'auto',
                                            maxHeight: 200,
                                            fontSize: '0.75rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}>
                                            {error.toString()}
                                            {error.stack && `\n\n${error.stack}`}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                </Container>
            </ErrorContainer>
        </Box>
    );
};

// Error Logger Function
const logError = (error, errorInfo) => {
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Error Info:', errorInfo);

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
};

// Main ErrorBoundary Component
export const ErrorBoundary = ({ children }) => {
    return (
        <ReactErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={logError}
            onReset={() => {
                // Optional: Reset any state or perform cleanup
                console.log('Error boundary reset');
            }}
        >
            {children}
        </ReactErrorBoundary>
    );
};
